import { ref } from 'vue';
import { defineStore } from 'pinia';
import type { OrderInterface } from './type';
import { OrderRepository } from './repository';
import { useToast } from 'primevue/usetoast'
import { useUsers } from '@/features/users/controller'

export const useOrders = defineStore('orders', () => {

    const orderRepository = new OrderRepository();

    const toPrepareOrders = ref<OrderInterface[]>([])
    const retiredOrders = ref<OrderInterface[]>([])

    const toast = useToast()
    const $userStore = useUsers()

    const toPrepareOrdersPagination = ref({
        currentPage: 1,
        lastPage: 1,
        perPage: 4,
        total: 0,
        from: 0,
        to: 0
    });

    const retiredOrdersPagination = ref({
        currentPage: 1,
        lastPage: 1,
        perPage: 4,
        total: 0,
        from: 0,
        to: 0
    });

    const activeOrder = ref<OrderInterface | null>(null)
    const activeOrderMag = ref<OrderInterface | null>(null)
    const wantsBigBags = ref<boolean>(false);
    const deliverySettings = ref<any>(null);

    const getDeliverySettings = async () => {
        const res = await orderRepository.getDeliverySettings();
        if (!res.error) {
            deliverySettings.value = res.data;
        }
        return res;
    };

    const getToPrepareOrders = async (page: number = 1, perPage: number = 10) => {
        const res = await orderRepository.getToPrepareOrders({ page, perPage })
        if (!res.error) {
            toPrepareOrders.value = res.data.data;
            toPrepareOrdersPagination.value = {
                currentPage: res.data.current_page,
                lastPage: res.data.last_page,
                perPage: res.data.per_page,
                total: res.data.total,
                from: res.data.from,
                to: res.data.to
            };
        }
        return res
    }

    const getRetiredOrders = async (page: number = 1, perPage: number = 10) => {
        const res = await orderRepository.getRetiredOrders({ page, perPage })
        if (!res.error) {
            retiredOrders.value = res.data.data;
            retiredOrdersPagination.value = {
                currentPage: res.data.current_page,
                lastPage: res.data.last_page,
                perPage: res.data.per_page,
                total: res.data.total,
                from: res.data.from,
                to: res.data.to
            };
        }
        return res
    }
    const processPickup = async (id: number, payload: any) => {
        const res = await orderRepository.processPickup(id, payload)
        if (res.error) {
            toast.add({
                severity: 'error',
                summary: 'Erreur',
                detail: res.data?.message || 'Erreur lors de l\'enlèvement.',
                life: 4000
            });
        } else {
            toast.add({
                severity: 'success',
                summary: 'Succès',
                detail: 'L\'enlèvement a été enregistré avec succès.',
                life: 4000
            });
        }
        return res
    }
    const processReady = async (id: number) => {
        const res = await orderRepository.processReady(id)
        if (res.error) {
            toast.add({
                severity: 'error',
                summary: 'Erreur',
                detail: res.data?.message || 'Erreur lors de la validation.',
                life: 4000
            });
        } else {
            toast.add({
                severity: 'success',
                summary: 'Succès',
                detail: 'La commande a été validée avec succès.',
                life: 4000
            });
        }
        return res
    }

    const cancelOrder = async (id: number, payload: { cancellation_reason: string, custom_comment?: string }) => {
        const res = await orderRepository.cancelOrder(id, payload)
        if (res.error) {
            toast.add({
                severity: 'error',
                summary: 'Erreur',
                detail: res.data?.message || res.data?.error || 'Erreur lors de l\'annulation.',
                life: 4000
            });
        } else {
            toast.add({
                severity: 'success',
                summary: 'Succès',
                detail: 'La commande a été annulée avec succès.',
                life: 4000
            });
        }
        return res
    }

    const getOrder = async (id: number) => {
        const res = await orderRepository.getOrder(id)
        if (!res.error) {
            activeOrderMag.value = res.data.order
        }
        return res
    }

    const validateOrder = async (amount: number, currency: string = 'eur', order: OrderInterface, construction_site: string) => {
        return await orderRepository.validateOrder(amount, currency, order, construction_site);
    };

    const validateStorekeeperOrder = async (amount: number, currency: string = 'eur', order: OrderInterface, construction_site: string, company_id?: number, user_id?: number, comment?: string, email?: string) => {
        return await orderRepository.validateStorekeeperOrder(amount, currency, order, construction_site, company_id, user_id, comment, email);
    };

    const fetchCartTotals = async (payload: any) => {
        let userId = null
        if ($userStore.selectedUserForShop && $userStore.loggedUser && ($userStore.loggedUser.type === 'administrator' || $userStore.loggedUser.type === 'storekeeper') && typeof $userStore.selectedUserForShop === 'object') {
            userId = $userStore.selectedUserForShop.id
        } else if ($userStore.loggedUser) {
            userId = $userStore.loggedUser.id
        }
        return await orderRepository.fetchCartTotals({ ...payload, userId });
    };

    return {
        toPrepareOrders,
        retiredOrders,
        toPrepareOrdersPagination,
        retiredOrdersPagination,
        getToPrepareOrders,
        getRetiredOrders,
        processPickup,
        processReady,
        cancelOrder,

        activeOrder,
        activeOrderMag,
        wantsBigBags,
        getOrder,

        validateOrder,
        validateStorekeeperOrder,
        fetchCartTotals,
        // syncBigBagsToBackend,
        // syncProductToBackend,
        // syncCartToBackend,

        getDeliverySettings,
        deliverySettings
    }

}, {
    persist: {
        paths: ['activeOrder', 'wantsBigBags'],
    },
})